import React, { useEffect, useState } from "react";
import { api, formatDateTime, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { CreditCard, CheckCircle, XCircle, Warning, Circle, ToggleLeft, ToggleRight, WarningOctagon } from "@phosphor-icons/react";

function StatusBadge({ status }) {
  const map = {
    active: { label: "Ativo", cls: "green", Icon: CheckCircle },
    invalid: { label: "Invalido", cls: "red", Icon: XCircle },
    untested: { label: "Nao testado", cls: "medio", Icon: Warning },
    unconfigured: { label: "Nao configurado", cls: "neutral", Icon: Circle },
  };
  const s = map[status] || map.unconfigured;
  const Icon = s.Icon;
  return (
    <span className={`badge ${s.cls} inline-flex items-center gap-1`} data-testid="reseller-mp-status">
      <Icon size={12} weight="fill" /> {s.label}
    </span>
  );
}

export default function ResellerSettings() {
  const [cfg, setCfg] = useState(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    setErr("");
    try {
      setCfg((await api.get("/integrations/reseller/me")).data);
    } catch (e) {
      setCfg(null);
      const status = e.response?.status;
      setErr(
        status === 404
          ? "Endpoint nao encontrado. O backend pode estar desatualizado nesta instalacao - atualize a VPS."
          : (formatErr(e.response?.data?.detail) || "Nao foi possivel carregar. Verifique sua conexao e tente novamente.")
      );
    }
  };
  useEffect(() => { load(); }, []);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setBusy(""); }
  };

  const save = () => run("save", async () => {
    await api.put("/integrations/reseller/me", { access_token: token || undefined });
    setToken("");
  }, "Token salvo");

  const test = () => run("test", async () => {
    const { data } = await api.post("/integrations/reseller/me/test", { token: token || undefined });
    data.valid ? toast.success(data.message) : toast.error(data.message);
  });

  const toggle = () => run("toggle", async () => {
    await api.put("/integrations/reseller/me", { enabled: !cfg.enabled });
  });

  if (!cfg) {
    if (err) {
      return (
        <div data-testid="reseller-settings-error" className="max-w-xl">
          <div className="topbar">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Minha conta</div>
              <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Configuracoes</h1>
            </div>
          </div>
          <div className="border border-[#DDA7A5] bg-[#F4E6E5] p-6 flex items-start gap-3">
            <WarningOctagon size={26} weight="duotone" className="shrink-0 text-[#7A2D2B]" />
            <div>
              <div className="font-display text-lg font-bold text-[#7A2D2B]">Erro ao carregar</div>
              <div className="text-sm text-[#7A2D2B] mt-1">{err}</div>
              <button onClick={load} className="brand mt-4" data-testid="reseller-settings-retry-btn">Tentar novamente</button>
            </div>
          </div>
        </div>
      );
    }
    return <div className="text-sm tracking-widest uppercase text-[#5C5A57]" data-testid="reseller-settings-loading">Carregando...</div>;
  }

  return (
    <div data-testid="reseller-settings-page">
      <div className="topbar">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Minha conta</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Configuracoes</h1>
        </div>
        <CreditCard size={42} weight="duotone" />
      </div>

      <div className="border border-[#EBE5DF] bg-[#F5F0EB] p-4 max-w-2xl mb-6 text-xs text-[#5C5A57]" data-testid="reseller-wa-notice">
        O atendimento e as cobrancas por WhatsApp agora sao feitos pelo <strong>numero oficial unico</strong> da Cris Lingerie, gerido pela administracao. Suas conversas e cobrancas continuam separadas e organizadas no menu <strong>CRM</strong>. Voce nao precisa mais conectar seu proprio numero.
      </div>

      <section className="border border-[#EBE5DF] bg-white p-6 max-w-2xl" data-testid="reseller-mp-section">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="section-title">Mercado Pago (PIX)</h2>
            <div className="text-xs text-[#5C5A57] mt-1">Cadastre seu Access Token para receber os PIX das suas vendas direto na sua conta.</div>
          </div>
          <StatusBadge status={cfg.status} />
        </div>

        <div className="border border-[#DDA7A5] bg-[#F4E6E5] p-3 mb-4 text-xs text-[#7A2D2B]">
          So e usado quando o administrador ativa o modo "Por revendedora". No modo "Plataforma unica" as cobrancas usam o token da plataforma.
        </div>

        {cfg.configured && (
          <div className="text-xs text-[#5C5A57] mb-3 tabular">
            Token salvo: <strong>{cfg.masked}</strong>
            {cfg.last_tested_at && <> · testado em {formatDateTime(cfg.last_tested_at)}</>}
          </div>
        )}

        <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">
          {cfg.configured ? "Substituir Access Token" : "Cadastrar Access Token"}
        </label>
        <input type="password" autoComplete="off" className="input-underline" placeholder="APP_USR-... ou TEST-..."
          value={token} onChange={(e) => setToken(e.target.value)} data-testid="reseller-mp-token-input" />

        <div className="flex flex-wrap gap-3 mt-5">
          <button onClick={save} disabled={busy === "save"} className="brand" data-testid="reseller-mp-save-btn">{busy === "save" ? "Salvando..." : "Salvar"}</button>
          <button onClick={test} disabled={busy === "test"} className="brand" style={{ background: "#5C5A57" }} data-testid="reseller-mp-test-btn">{busy === "test" ? "Testando..." : "Testar conexao"}</button>
          <button onClick={toggle} disabled={busy === "toggle" || !cfg.configured} className="flex items-center gap-2 text-xs tracking-widest uppercase font-medium hover:underline disabled:opacity-40" data-testid="reseller-mp-toggle-btn">
            {cfg.enabled ? <ToggleRight size={22} weight="fill" /> : <ToggleLeft size={22} />}
            {cfg.enabled ? "Ativado" : "Desativado"}
          </button>
        </div>
      </section>
    </div>
  );
}
